import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { defaultCity, loadCity, saveCity, detectGeolocation, STORAGE_KEY } from '@/lib/city';

const Ctx = createContext(null);

export function CityProvider({ children }) {
  const [city, setCity] = useState(() => loadCity() || defaultCity);
  const [detecting, setDetecting] = useState(false);
  const [hasConsent, setHasConsent] = useState(() => !!loadCity());

  useEffect(() => { saveCity(city); }, [city]);

  // Cross-tab sync: when another tab/window updates the saved city, mirror it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const next = JSON.parse(e.newValue);
        if (next && typeof next.lat === 'number' && typeof next.lon === 'number') {
          setCity(next);
          setHasConsent(true);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const updateCity = useCallback((c) => {
    if (!c || typeof c.lat !== 'number' || typeof c.lon !== 'number') return;
    setCity({
      name: c.name || c.label?.split(',')[0] || 'City',
      country: c.country || '',
      country_code: c.country_code || '',
      lat: c.lat,
      lon: c.lon,
      label: c.label || `${c.name}${c.country ? ', ' + c.country : ''}`,
    });
    setHasConsent(true);
  }, []);

  const detectAndSet = useCallback(async () => {
    setDetecting(true);
    try {
      const c = await detectGeolocation();
      updateCity(c);
      return c;
    } finally {
      setDetecting(false);
    }
  }, [updateCity]);

  const value = useMemo(() => ({ city, setCity: updateCity, detectAndSet, detecting, hasConsent, setHasConsent }), [city, updateCity, detectAndSet, detecting, hasConsent]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCity() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCity must be used inside CityProvider');
  return ctx;
}
